import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { History, Trophy, User, Calendar, ChevronDown, ChevronUp, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TestHistoryPage() {
  const [results, setResults] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStaff, setFilterStaff] = useState("");
  const [expandedResult, setExpandedResult] = useState(null);

  useEffect(() => {
    Promise.all([fetchResults(), fetchStaff()]).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchResults();
  }, [filterStaff]);

  const fetchResults = async () => {
    try {
      const params = filterStaff ? `?staff_id=${filterStaff}` : "";
      const response = await axios.get(`${API}/test-results${params}`);
      setResults(response.data);
    } catch (error) {
      console.error("Failed to fetch results:", error);
    }
  };

  const fetchStaff = async () => {
    try {
      const response = await axios.get(`${API}/staff`);
      setStaff(response.data);
    } catch (error) {
      console.error("Failed to fetch staff:", error);
    }
  };

  const getScoreColor = (percentage) => {
    if (percentage >= 80) return "text-green-500";
    if (percentage >= 60) return "text-[#C36A2F]";
    return "text-red-500";
  };

  const getScoreBg = (percentage) => {
    if (percentage >= 80) return "bg-green-500/10 border-green-500/20";
    if (percentage >= 60) return "bg-[#C36A2F]/10 border-[#C36A2F]/20";
    return "bg-red-500/10 border-red-500/20";
  };

  const handleDeleteResult = async (e, resultId) => {
    e.stopPropagation();
    if (!window.confirm("Delete this test result? This will affect leaderboards.")) return;
    try {
      await axios.delete(`${API}/test-results/${resultId}`);
      setResults(prev => prev.filter(r => r.id !== resultId));
      toast.success("Test result deleted");
    } catch {
      toast.error("Failed to delete result");
    }
  };

  return (
    <div data-testid="test-history-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Test History</h1>
          <p className="text-[hsl(var(--muted-foreground))] mt-2">
            View all completed test results
          </p>
        </div>
        <Select value={filterStaff} onValueChange={setFilterStaff}>
          <SelectTrigger className="w-48" data-testid="filter-staff-select">
            <SelectValue placeholder="Filter by staff" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Staff</SelectItem>
            {staff.map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Results List */}
      {loading ? (
        <div className="py-12 text-center text-[hsl(var(--muted-foreground))]" data-testid="history-loading">
          Loading...
        </div>
      ) : results.length === 0 ? (
        <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
          <CardContent className="py-12 text-center" data-testid="no-results">
            <History size={48} className="mx-auto text-[hsl(var(--muted-foreground))] mb-4" />
            <p className="text-[hsl(var(--muted-foreground))]">No test results yet</p>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              Results will appear here after staff complete tests
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4" data-testid="results-list">
          {results.map((result) => (
            <Card 
              key={result.id} 
              className="bg-[hsl(var(--card))] border-[hsl(var(--border))]"
              data-testid={`result-card-${result.id}`}
            >
              <CardContent className="p-0">
                {/* Summary Row */}
                <div 
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-[hsl(var(--accent))] transition-colors"
                  onClick={() => setExpandedResult(expandedResult === result.id ? null : result.id)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-[hsl(var(--primary))]/20 flex items-center justify-center">
                      <User className="text-[hsl(var(--primary))]" size={24} />
                    </div>
                    <div>
                      <p className="font-semibold text-lg">{result.staff_name}</p>
                      <div className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))]">
                        <Calendar size={14} />
                        {new Date(result.completed_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className={`px-4 py-2 rounded-lg border ${getScoreBg(result.percentage)}`}>
                      <p className={`text-2xl font-bold ${getScoreColor(result.percentage)}`}>
                        {result.percentage.toFixed(0)}%
                      </p>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">
                        {result.total_score}/{result.total_possible} pts
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => handleDeleteResult(e, result.id)}
                      className="text-[hsl(var(--muted-foreground))] hover:text-red-500 shrink-0"
                      data-testid={`delete-result-${result.id}`}
                    >
                      <Trash2 size={16} />
                    </Button>
                    {expandedResult === result.id ? (
                      <ChevronUp className="text-[hsl(var(--muted-foreground))]" size={20} />
                    ) : (
                      <ChevronDown className="text-[hsl(var(--muted-foreground))]" size={20} />
                    )}
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedResult === result.id && (
                  <div className="border-t border-[hsl(var(--border))] p-4 space-y-3" data-testid={`result-details-${result.id}`}>
                    <h4 className="font-semibold text-[hsl(var(--primary))]">Test Details</h4>
                    <div className="space-y-2">
                      {/* Handle both old (array) and new (dict) answer formats */}
                      {Array.isArray(result.answers) ? (
                        result.answers.map((answer, idx) => (
                          <div 
                            key={`${answer.cocktail_id || answer.cocktail_name || 'a'}-${idx}`}
                            className="p-3 rounded-lg bg-[hsl(var(--secondary))] flex items-center justify-between"
                          >
                            <div>
                              <p className="font-medium">{answer.cocktail_name}</p>
                              <div className="flex gap-3 mt-1 text-xs">
                                <span className={answer.ingredients_correct ? "text-green-500" : "text-red-500"}>
                                  Ingredients {answer.ingredients_correct ? "✓" : "✗"}
                                </span>
                                <span className={answer.garnish_correct ? "text-green-500" : "text-red-500"}>
                                  Garnish {answer.garnish_correct ? "✓" : "✗"}
                                </span>
                                <span className={answer.glassware_correct ? "text-green-500" : "text-red-500"}>
                                  Glassware {answer.glassware_correct ? "✓" : "✗"}
                                </span>
                                <span className={answer.side_elements_correct ? "text-green-500" : "text-red-500"}>
                                  Side {answer.side_elements_correct ? "✓" : "✗"}
                                </span>
                              </div>
                            </div>
                            <div className={`text-lg font-bold ${
                              answer.score >= 3 ? 'text-green-500' : answer.score >= 2 ? 'text-[#C36A2F]' : 'text-red-500'
                            }`}>
                              {answer.score}/4
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="p-3 rounded-lg bg-[hsl(var(--secondary))]">
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <span className="text-[hsl(var(--muted-foreground))]">Correct Answers:</span>
                            <span className="text-green-500 font-semibold">{result.correct_answers || 0}</span>
                            <span className="text-[hsl(var(--muted-foreground))]">Perfect Cocktails:</span>
                            <span className="font-semibold">{result.perfect_cocktails || 0}</span>
                            <span className="text-[hsl(var(--muted-foreground))]">Best Streak:</span>
                            <span className="font-semibold">{result.best_streak || 0}</span>
                          </div>
                          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-2">
                            {Object.keys(result.answers || {}).length} questions answered
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
